/**
 * Sprint B — Modale Demande de devis complémentaire.
 *
 * Permet à l'archi de :
 *  - Choisir l'entreprise destinataire (autoremplie depuis l'élément si possible)
 *  - Décrire la prestation hors marché
 *  - Saisir un montant estimé indicatif
 *  - Déclencher l'envoi email Brevo
 */
import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { X, PaperPlaneRight } from '@phosphor-icons/react';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function DevisComplementaireDialog({ open, onClose, chantierId, element, onCreated }) {
  const [entreprises, setEntreprises] = useState([]);
  const [entrepriseNom, setEntrepriseNom] = useState('');
  const [entrepriseEmail, setEntrepriseEmail] = useState('');
  const [description, setDescription] = useState('');
  const [montant, setMontant] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open || !chantierId) return;
    setDescription(element?.description || element?.titre || '');
    setEntrepriseNom(element?.entreprise || '');
    setEntrepriseEmail('');
    setMontant('');

    axios.get(`${API_URL}/api/chantiers/${chantierId}/marches`, { withCredentials: true })
      .then((r) => {
        const list = (Array.isArray(r.data) ? r.data : []).map((m) => ({
          nom: m.entreprise_nom,
          email: m.entreprise_email,
        })).filter((x) => x.nom);
        setEntreprises(list);
      })
      .catch(() => setEntreprises([]));
  }, [open, chantierId, element]);

  if (!open) return null;
  const eid = element?._id || element?.id;

  const handleSubmit = async () => {
    if (submitting) return;
    if (!description.trim()) { toast.error('Description requise'); return; }
    setSubmitting(true);
    try {
      const payload = {
        element_id: eid,
        entreprise_nom: entrepriseNom?.trim() || null,
        entreprise_email: entrepriseEmail?.trim() || null,
        description: description.trim(),
        montant_estime: montant ? parseFloat(montant) : null,
      };
      const { data } = await axios.post(
        `${API_URL}/api/chantiers/${chantierId}/devis-complementaires`,
        payload,
        { withCredentials: true },
      );
      toast.success("Demande envoyée. L'action est marquée « À chiffrer ».");
      onCreated?.(data);
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur envoi demande');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 backdrop-blur-sm p-4"
      onClick={onClose}
      data-testid="devis-comp-dialog"
    >
      <div
        className="bg-white shadow-2xl w-full max-w-md max-h-full overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-4 border-b border-[#E0E0DB] flex items-center justify-between bg-[#FDF1E1]">
          <div>
            <h3 className="text-base font-bold text-[#2C2C2C]" data-testid="devis-comp-title">Devis complémentaire</h3>
            <p className="text-xs text-[#6B6B6B] mt-0.5">Demander un chiffrage hors marché initial.</p>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 hover:bg-white rounded-none"
            data-testid="devis-comp-close"
            aria-label="Fermer"
          >
            <X size={18} />
          </button>
        </div>

        <div className="p-4 space-y-3 text-sm">
          <div className="bg-[#FAFAF8] border-l-4 border-[#C4773B] p-2 text-xs text-[#6B6B6B]">
            Action : <span className="font-semibold text-[#2C2C2C]">{element?.titre || '(sans titre)'}</span>
          </div>

          <div>
            <label className="block text-[10px] uppercase tracking-wider text-[#6B6B6B] mb-1 font-bold">Entreprise (nom)</label>
            <input
              list="entreprises-list"
              value={entrepriseNom}
              onChange={(e) => {
                setEntrepriseNom(e.target.value);
                const match = entreprises.find((x) => x.nom === e.target.value);
                if (match?.email) setEntrepriseEmail(match.email);
              }}
              placeholder="Nom de l'entreprise"
              className="w-full px-3 py-2 border border-[#E0E0DB] rounded-none text-sm"
              data-testid="devis-comp-entreprise-nom"
            />
            <datalist id="entreprises-list">
              {entreprises.map((e, i) => <option key={i} value={e.nom} />)}
            </datalist>
          </div>

          <div>
            <label className="block text-[10px] uppercase tracking-wider text-[#6B6B6B] mb-1 font-bold">Email destinataire</label>
            <input
              type="email"
              value={entrepriseEmail}
              onChange={(e) => setEntrepriseEmail(e.target.value)}
              placeholder="optionnel — sinon auto-résolu"
              className="w-full px-3 py-2 border border-[#E0E0DB] rounded-none text-sm"
              data-testid="devis-comp-entreprise-email"
            />
          </div>

          <div>
            <label className="block text-[10px] uppercase tracking-wider text-[#6B6B6B] mb-1 font-bold">Description</label>
            <Textarea
              rows={4}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="rounded-none"
              data-testid="devis-comp-description"
            />
          </div>

          <div>
            <label className="block text-[10px] uppercase tracking-wider text-[#6B6B6B] mb-1 font-bold">Montant estimé (€)</label>
            <input
              type="number"
              step="0.01"
              min="0"
              value={montant}
              onChange={(e) => setMontant(e.target.value)}
              placeholder="optionnel"
              className="w-full px-3 py-2 border border-[#E0E0DB] rounded-none text-sm"
              data-testid="devis-comp-montant"
            />
          </div>
        </div>

        <div className="p-4 border-t border-[#E0E0DB] flex justify-end gap-2 bg-[#FAFAF8]">
          <Button
            variant="outline"
            className="rounded-none"
            onClick={onClose}
            disabled={submitting}
            data-testid="devis-comp-cancel"
          >
            Annuler
          </Button>
          <Button
            className="rounded-none bg-[#C4773B] hover:bg-[#a85f29] text-white"
            onClick={handleSubmit}
            disabled={submitting}
            data-testid="devis-comp-send"
          >
            <PaperPlaneRight size={14} className="mr-1" />
            {submitting ? 'Envoi…' : 'Envoyer la demande'}
          </Button>
        </div>
      </div>
    </div>
  );
}
